import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { Pencil, Plus, UserCheck, UserX } from "lucide-react";
import { toast } from "sonner";

const blank = { name: "", staff_no: "", phone: "", is_active: true };

// Individual members (staff / dependants) within a corporate client; sales still bill the company account.
export function CorporateMembersDialog({ company, onClose, onChanged, canManage }) {
  const [members, setMembers] = useState(company?.members || []);
  const [form, setForm] = useState(blank);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async (body = form, id = editId) => {
    if (!body.name.trim()) return toast.error("Member name is required");
    setBusy(true);
    try {
      const { data } = id ? await api.put(`/customers/${company.id}/members/${id}`, body) : await api.post(`/customers/${company.id}/members`, body);
      setMembers((ms) => (id ? ms.map((m) => (m.id === id ? data : m)) : [...ms, data]));
      if (!id || id === editId) { setForm(blank); setEditId(null); }
      onChanged();
      toast.success(id ? "Member updated" : "Member added");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const toggle = (m) => save({ name: m.name, staff_no: m.staff_no || "", phone: m.phone || "", is_active: !(m.is_active ?? true) }, m.id);
  return (
    <Dialog open={!!company} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Members — {company?.name}</DialogTitle>
          <DialogDescription>Each member's purchases go on the company account and share its credit limit. When a company has active members, the POS asks which member the sale is for.</DialogDescription>
        </DialogHeader>
        {canManage && (
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_8rem_9rem_auto] gap-2 items-end rounded-md border border-border p-3" data-testid="member-form">
            <Input placeholder="Full name *" value={form.name} onChange={set("name")} data-testid="member-name-input" />
            <Input placeholder="Staff / ID no." value={form.staff_no} onChange={set("staff_no")} data-testid="member-staff-input" />
            <Input placeholder="Phone" value={form.phone} onChange={set("phone")} data-testid="member-phone-input" />
            <div className="flex gap-1">
              <Button disabled={busy} onClick={() => save()} data-testid="member-save-btn">{editId ? "Save" : <><Plus className="h-4 w-4 mr-1" /> Add</>}</Button>
              {editId && <Button variant="ghost" onClick={() => { setEditId(null); setForm(blank); }}>Cancel</Button>}
            </div>
          </div>
        )}
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-muted-foreground text-left border-b border-border"><tr><th className="py-2">Name</th><th className="py-2">Staff / ID</th><th className="py-2">Phone</th><th className="py-2">Status</th><th className="py-2"></th></tr></thead>
          <tbody>
            {!members.length && <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">No members yet — sales are billed to the company only</td></tr>}
            {members.map((m) => (
              <tr key={m.id} className="border-b border-border" data-testid={`member-row-${m.staff_no || m.id}`}>
                <td className="py-2 font-medium">{m.name}</td>
                <td className="py-2 font-mono text-xs">{m.staff_no || "—"}</td>
                <td className="py-2 text-xs">{m.phone || "—"}</td>
                <td className="py-2"><StatusBadge value={(m.is_active ?? true) ? "active" : "inactive"} /></td>
                <td className="py-2 text-right whitespace-nowrap">{canManage && <>
                  <Button variant="ghost" size="sm" onClick={() => { setEditId(m.id); setForm({ name: m.name, staff_no: m.staff_no || "", phone: m.phone || "", is_active: m.is_active ?? true }); }} data-testid={`member-edit-${m.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button variant="ghost" size="sm" title={(m.is_active ?? true) ? "Deactivate" : "Reactivate"} onClick={() => toggle(m)} data-testid={`member-toggle-${m.id}`}>{(m.is_active ?? true) ? <UserX className="h-3.5 w-3.5" /> : <UserCheck className="h-3.5 w-3.5" />}</Button>
                </>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </DialogContent>
    </Dialog>
  );
}
